import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { getReviewPaymentViewModel } from '@app/payments/selectors/payments.selector';
import { Store } from '@ngrx/store';
import { PanAppState } from '@panamax/app-state';
import { PaymentsService } from '@payments/services/payments.service';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { BankStoreService, PaymentStoreService } from '@usf/ngrx-payment';
import {
  CustomerBankAccount,
  CustomerReview,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import { Observable, combineLatest, filter, map, take } from 'rxjs';
import { ReviewPaymentPageVM } from '../review-payment.page';

@Injectable({
  providedIn: 'root',
})
export class ReviewPaymentService {
  constructor(
    private store: Store,
    private bankStoreService: BankStoreService,
    private paymentStoreService: PaymentStoreService,
    private paymentsService: PaymentsService,
    private router: Router,
    private panAppState: PanAppState,
  ) {}

  getPayment$(): Observable<ReviewPaymentPageVM> {
    return combineLatest([
      this.store.select(getReviewPaymentViewModel),
      this.panAppState.feature$([
        FEATURES.split_user_payments_payment_preferences.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_routing_validation.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
    ]).pipe(
      map(
        ([
          vm,
          isPaymentPreferencesOn,
          isBankRoutingValidationOn,
          orderNumberFlag,
        ]) => {
          return {
            ...vm,
            isPaymentPreferencesOn,
            isBankRoutingValidationOn,
            showOrderNumber: orderNumberFlag,
          };
        },
      ),
    );
  }

  deselectAutopaySelectedTransactions(customers: CustomerReview[]) {
    const autoPayTransactions: PaymentTransactionsViewModel[] =
      customers.reduce(
        (prev, customer) => prev.concat(customer.autoPayTransactions),
        [],
      );

    this.paymentsService.selectInvoices(autoPayTransactions);
  }

  fetchBankDetails(customers: { customerNumber: number }[]) {
    this.bankStoreService.fetchBanksDetails(customers, false);
  }

  showSubmitError(message: string) {
    this.paymentsService.showToastMessage(message, true);
    this.paymentStoreService.clearError();
  }

  showBankAccountError(message: string) {
    this.paymentsService.showToastMessage(message, true);
    this.bankStoreService.clearError();
  }

  submitPayment(scheduledPaymentDate: number) {
    this.paymentStoreService.submitPayment(scheduledPaymentDate);
  }

  canActivate(): Observable<boolean> {
    return combineLatest([this.getPayment$(), this.panAppState.user$]).pipe(
      filter(([, user]) => !!user),
      map(([vm]) => {
        !vm.customers.length && this.router.navigate([PATHS.PAYMENTS]);
        return !!vm.customers.length;
      }),
      take(1),
    );
  }

  setBankAccountsToPay(bankAccounts: CustomerBankAccount[]) {
    this.bankStoreService.setBanksToPay(bankAccounts);
  }
}
